import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Inbox } from 'lucide-react';
import logo from '../../assets/LOGOF.png';
// removed old retina fallback image
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { LoginForm } from '../../types';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showLoginError, setShowLoginError] = useState(false);
  const { login, isAuthenticated, isLoading, error } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginForm) => {
    login(data);
  };

  // On mount: if redirected after a 401 we set a flag in localStorage; show modal once
  useEffect(() => {
    try {
      const v = localStorage.getItem('sessionExpired');
      if (v) {
        setShowSessionExpired(true);
        localStorage.removeItem('sessionExpired');
      }
    } catch (e) {}
  }, []);

  // Show a prominent modal when login errors occur so user always sees it
  useEffect(() => {
    if (error) {
      setShowLoginError(true);
    }
  }, [error]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="flex w-full max-w-lg items-start justify-center">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 sm:p-10">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center">
              <img src={logo} alt="Logo" className="h-48 w-48 sm:h-64 sm:w-64 object-contain mb-1" style={{display: 'block'}} />
            </div>

            <h2 className="mt-3 text-center text-3xl font-extrabold text-gray-900">
              Iniciar Sesión
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              placeholder="Correo electrónico"
              error={errors.email?.message}
              {...register('email')}
            />

            <div className="relative">
              <Input
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                error={errors.password?.message}
                {...register('password')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center top-6"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

            <div>
              <Button
                type="submit"
                className="w-full py-3 text-lg rounded-lg"
                loading={isLoading}
              >
                Iniciar Sesión
              </Button>
            </div>

            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-sm text-primary-600 hover:underline"
              >
                Olvide mi contraseña
              </button>
            </div>

            {/* Pre-SISI es un módulo aparte, con su propio login y sesión (ver
                src/presisi/). No comparte esta pantalla ni el estado de useAuth. */}
            <div className="pt-4 border-t border-gray-100 text-center">
              <Link
                to="/pre-sisi/login"
                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600"
              >
                <Inbox className="h-4 w-4" /> Ir a Pre-SISI
              </Link>
            </div>
          </form>
        </div>
        </div>
        

        {/* Footer legend bottom-left */}
          {/* footer legend removed per branding update */}

      {showForgot && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowForgot(false)} />
          <div className="bg-white rounded-lg shadow-2xl p-6 z-10 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Recuperar contraseña</h3>
            <p className="mt-3 text-sm text-gray-700">Contacta a soporte a este correo:</p>
            <p className="mt-2 text-sm font-medium text-primary-600">claudio.munoz@belray.cl</p>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowForgot(false)}
                className="px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showLoginError && error && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowLoginError(false)} />
          <div className="bg-white rounded-lg shadow-2xl p-4 z-10 max-w-sm w-full mx-4 mb-6 sm:mb-0">
            <div className="text-sm text-red-700 font-medium">{error}</div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => setShowLoginError(false)}
                className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showSessionExpired && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowSessionExpired(false)} />
          <div className="bg-white rounded-lg shadow-2xl p-6 z-10 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Sesión expirada</h3>
            <p className="mt-3 text-sm text-gray-700">Tu sesión ha expirado o se ha detectado acceso no autorizado. Por favor, inicia sesión de nuevo.</p>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowSessionExpired(false)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  </div>
  );
};
